/**
 * Cart drawer.
 *
 * Wraps a native `<dialog>`, so the focus trap, Escape-to-close, focus return
 * to the trigger and the inert background are all the browser's. This element
 * only adds what the platform does not give us:
 *
 *   1. opening from the header cart bubble, after an add to cart, and on load
 *      when the page was linked to with `?cart=true`
 *   2. closing on a backdrop click
 *   3. re-rendering its content from the section HTML cart.js bundles into
 *      every mutation ('cart:change'), without losing scroll or focus
 *   4. a live region announcing the new total
 *   5. locking the page behind it — showModal() makes the background inert,
 *      but it does not stop it scrolling
 *
 * @extends HTMLElement
 */
export class CartDrawer extends HTMLElement {
  static SCROLL_LOCK_CLASS = 'cart-drawer-open';
  /* `?cart=true` on any page opens the drawer on load — see openFromUrl. */
  static OPEN_PARAM = 'cart';

  constructor() {
    super();
    this.onCartChange = this.onCartChange.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onInnerClick = this.onInnerClick.bind(this);
    this.onQuickAddCapture = this.onQuickAddCapture.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);
    this.unlock = this.unlock.bind(this);
  }

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.announcer = this.querySelector('[data-cart-drawer-announcer]');

    document.addEventListener('cart:change', this.onCartChange);
    document.addEventListener('click', this.onDocumentClick);
    this.addEventListener('click', this.onInnerClick);
    /* Capture, because <modal-opener> calls stopPropagation() on its own
       button — a bubble-phase listener up here never sees the click. */
    this.addEventListener('click', this.onQuickAddCapture, true);
    this.dialog?.addEventListener('close', this.onDialogClose);
    /* `cancel` fires on Escape, before `close`. Listening to both means the
       page cannot be left scroll-locked if either event is missed. */
    this.dialog?.addEventListener('cancel', this.unlock);

    this.openFromUrl();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this.onCartChange);
    document.removeEventListener('click', this.onDocumentClick);
    this.removeEventListener('click', this.onInnerClick);
    this.removeEventListener('click', this.onQuickAddCapture, true);
    this.spinnerObserver?.disconnect();
    this.dialog?.removeEventListener('close', this.onDialogClose);
    this.dialog?.removeEventListener('cancel', this.unlock);
    document.documentElement.classList.remove(CartDrawer.SCROLL_LOCK_CLASS);
  }

  get sectionId() {
    return this.closest('.shopify-section')?.id.replace('shopify-section-', '');
  }

  get content() {
    return this.querySelector('[data-cart-drawer-content]');
  }

  get isOpen() {
    return Boolean(this.dialog?.open);
  }

  /**
   * Opens the drawer when the page was linked to with `?cart=true`.
   *
   * For "back to the store, cart open" links — an email, a QR code, a redirect
   * back from an app — where the shopper should land looking at their cart
   * without a click.
   *
   * The parameter is then removed from the address bar, because it is an
   * instruction for this one navigation rather than a state. Left in place,
   * closing the drawer and refreshing would reopen it, and any link the shopper
   * copied out of the address bar would carry it to whoever they sent it to.
   * `replaceState` and not `pushState`, so it does not become a history entry
   * the back button walks into and reopens.
   */
  openFromUrl() {
    const url = new URL(window.location.href);
    if (url.searchParams.get(CartDrawer.OPEN_PARAM) !== 'true') return;

    this.open();

    url.searchParams.delete(CartDrawer.OPEN_PARAM);
    window.history.replaceState({}, '', url);
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    /* Lock first: showModal() scrolls the newly focused control into view, and
       with the page still scrollable that shows as a jump behind the drawer. */
    document.documentElement.classList.add(CartDrawer.SCROLL_LOCK_CLASS);
    this.dialog.showModal();
    this.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
  }

  close() {
    this.dialog?.close();
    /* Unlock here as well as on the `close` event. Escape and other browser-
       initiated closes never call this method, so the event listener is needed;
       and the event is queued rather than synchronous, so a page left scroll-
       locked is the worse failure to risk. Both paths, one idempotent unlock. */
    this.unlock();
  }

  onDialogClose() {
    this.unlock();
  }

  unlock() {
    document.documentElement.classList.remove(CartDrawer.SCROLL_LOCK_CLASS);
  }

  /* The header bubble is a link to the cart page, and stays one: with no
     JavaScript it navigates, which is the correct fallback. */
  onDocumentClick(event) {
    const trigger = event.target.closest('#header-cart-bubble');
    if (!trigger) return;
    event.preventDefault();
    if (this.isOpen) this.close();
    else this.open();
  }

  onInnerClick(event) {
    /* With showModal(), a click on the backdrop targets the dialog itself. */
    if (event.target === this.dialog) {
      this.close();
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) this.close();
  }

  /**
   * Lets a recommendation card open the shared quick-add modal *over* the
   * drawer, instead of the drawer having to get out of the way.
   *
   * `component-quick-add.js` parks every `<quick-add-modal>` on `<body>` the
   * moment it connects. showModal() makes everything outside this dialog
   * inert, so a modal left there opens behind our own backdrop and cannot be
   * clicked. The fix is to adopt it: moved inside the open dialog it is no
   * longer in the inert subtree, and because it is `position: fixed` it still
   * covers the whole viewport rather than being boxed into the panel.
   *
   * Nothing in the theme's quick add is altered — this only moves a node it
   * had already relocated once. Re-parenting runs its connectedCallback again,
   * which its own `initialized` guard makes a no-op, so it does not bounce
   * back to `<body>`.
   *
   * Capture phase, because <modal-opener> calls stopPropagation() on its own
   * button: this has to run before the opener resolves `data-modal` and shows
   * it. Appending is synchronous, so the id still resolves.
   */
  onQuickAddCapture(event) {
    const opener = event.target.closest('modal-opener');
    if (!opener || !this.dialog || !this.isOpen) return;

    const selector = opener.getAttribute('data-modal');
    const modal = selector && document.querySelector(selector);
    /* Only ever hoisted into an *open* dialog. Inside a closed one it would be
       display:none, and the shopper would click for nothing. */
    if (modal && modal.parentElement !== this.dialog) this.dialog.appendChild(modal);

    this.releaseSpinnerWhenSettled(opener.querySelector('button'));
  }

  /**
   * Puts the trigger's label back once the modal has settled.
   *
   * component-modal-opener.js swaps the label for a spinner on click but never
   * undoes it; component-quick-add.js does the undoing, and only for
   * `.quick-add__icon-button` — a 44px white pill with a hover-expand from the
   * product card, whose styling this 24px black button must not inherit just
   * to borrow a JS hook. So the reset lives here instead.
   *
   * The signal is `aria-disabled` clearing on the trigger, which quick add
   * removes in a `finally`. On success that lands with the dialog already on
   * screen, which is the moment asked for; and because it is a `finally` it
   * fires on a failed fetch too, so the button can never be left spinning at a
   * modal that never opened — which quick add's own reset, sitting in the
   * success path, does not cover.
   *
   * @param {HTMLElement|null} button
   */
  releaseSpinnerWhenSettled(button) {
    this.spinnerObserver?.disconnect();
    if (!button) return;

    this.spinnerObserver = new MutationObserver(() => {
      /* Set on the way in, removed on the way out; only the removal matters. */
      if (button.hasAttribute('aria-disabled')) return;

      this.spinnerObserver.disconnect();
      button.querySelector('.add-to-cart-icon-spinner')?.classList.add('hidden');
      button.querySelector('.add-to-cart-icon')?.classList.remove('hidden');
      button.querySelector('.add-to-cart-text__content')?.classList.remove('hidden');
    });

    this.spinnerObserver.observe(button, { attributes: true, attributeFilter: ['aria-disabled'] });
  }

  onCartChange(event) {
    const html = event.detail.sections?.[this.sectionId];
    if (html) this.render(html);
    if (event.detail.action === 'add') this.open();
    this.announce();
  }

  /**
   * Swaps the content region for the freshly rendered one.
   *
   * Only the region marked `data-cart-drawer-content` is replaced: the header,
   * the close button and the live region sit outside it precisely so a
   * re-render cannot destroy the element that currently holds focus.
   *
   * @param {string} html - Section HTML returned by the Section Rendering API.
   */
  render(html) {
    const current = this.content;
    if (!current) return;

    this.discardCardModals();

    const next = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('[data-cart-drawer-content]');
    if (!next) return;

    const body = current.querySelector('.cart-drawer__body');
    const scrollTop = body ? body.scrollTop : 0;
    const focus = this.captureFocus();

    current.innerHTML = next.innerHTML;

    const nextBody = current.querySelector('.cart-drawer__body');
    if (nextBody) nextBody.scrollTop = scrollTop;
    this.restoreFocus(focus);
  }

  /**
   * Drops the quick-add modals belonging to the recommendation cards, before
   * the content that spawned them is replaced.
   *
   * They have already moved themselves onto `<body>` (see onQuickAddCapture),
   * so replacing the content region does not take them with it. Without this
   * every cart mutation would leave another copy behind, each sharing an id
   * with the one the new markup brings and each holding its own `cart:change`
   * listener.
   *
   * `hide()` first, not just `remove()`: an open modal owns the
   * `overflow-hidden` class on `<body>`, and dropping the element without
   * letting it clean up leaves the page unable to scroll once the drawer is
   * shut. Scoped by id prefix because these are the only `QuickAdd-cart-` ids
   * in the theme — a grid tile's modal is `QuickAdd-<product id>` and is not
   * ours to remove.
   */
  discardCardModals() {
    for (const modal of document.querySelectorAll('quick-add-modal[id^="QuickAdd-cart-"]')) {
      modal.hide?.();
      modal.remove();
    }
  }

  /**
   * Records enough about the focused control to find its replacement.
   * @returns {{lineKey: string|null, tag: string, className: string}|null}
   */
  captureFocus() {
    const active = document.activeElement;
    if (!active || !this.content?.contains(active)) return null;

    return {
      lineKey: active.closest('[data-line-key]')?.dataset.lineKey || null,
      tag: active.tagName.toLowerCase(),
      className: active.classList[0] || '',
    };
  }

  /**
   * Puts focus back where it was, or on the close button.
   *
   * Never letting focus fall to <body> is the point: inside a modal dialog that
   * strands a keyboard user with nothing focused and no obvious way back.
   *
   * @param {{lineKey: string|null, tag: string, className: string}|null} focus
   */
  restoreFocus(focus) {
    if (!focus) return;

    let target = null;
    if (focus.lineKey && focus.className) {
      const line = this.content.querySelector(`[data-line-key="${CSS.escape(focus.lineKey)}"]`);
      target = line?.querySelector(`${focus.tag}.${CSS.escape(focus.className)}`) || null;
    }

    (target || this.querySelector('[data-cart-drawer-close]'))?.focus();
  }

  /**
   * Announces the new cart state by reading the rendered totals row, which is
   * already localised and already money-formatted by Liquid. Building the
   * sentence in JavaScript would mean reimplementing both.
   */
  announce() {
    if (!this.announcer) return;

    const label = this.querySelector('.cart-drawer__subtotal-label')?.textContent.trim();
    const value = this.querySelector('.cart-drawer__subtotal-value')?.textContent.trim();
    const empty = this.querySelector('.cart-drawer__empty-title')?.textContent.trim();

    if (label && value) this.announcer.textContent = `${label} ${value}`;
    else if (empty) this.announcer.textContent = empty;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
